/*
  BOPIS checkout pickup preference.

  After a BOPIS add-to-cart, sets the cart's delivery preference (Storefront API,
  tokenless, same-origin) so checkout opens on "Pick up" with the chosen store
  selected. Shipping stays available at checkout — this only prefills.

  BOPIS lines carry the line item property `_pickup_location` (Shopify Location ID).
  The preference is cleared once no BOPIS lines remain, so later carts don't
  default to pickup. Mixed carts keep the preference: the app's local-pickup
  generator only offers the store if it stocks every line, otherwise checkout
  falls back to shipping on its own.

  Never throws — failures are logged and the cart is left as-is.
*/
(() => {
  const API_URL = '/api/2026-07/graphql.json';
  const FLAG_KEY = 'pickupPreferenceSet';
  const GEO_FLAG_KEY = 'cartAttributesSet'; // owned by snippets/cart-attributes.liquid
  const MAX_PICKUP_MILES = 50; // must match the local-pickup generator function

  const MUTATION = `
    mutation PickupPreference($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          buyerIdentity {
            preferences { delivery { deliveryMethod pickupHandle } }
          }
        }
        userErrors { field message code }
      }
    }
  `;

  const storage = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {}
    },
    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {}
    },
  };

  // Accepts a numeric ID or a gid://shopify/Location/<id>; returns the numeric string.
  function normalizeLocationId(value) {
    const id = String(value ?? '').trim().split('/').pop();
    return /^\d+$/.test(id) ? id : null;
  }

  function haversineMiles(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * 3963 * Math.asin(Math.sqrt(a));
  }

  async function fetchCart() {
    const res = await fetch(window.Shopify.routes.root + 'cart.js');
    if (!res.ok) throw new Error('cart.js HTTP ' + res.status);
    return res.json();
  }

  // cart.js `token` already includes the `?key=` suffix the Storefront API expects.
  async function updateDeliveryPreference(cart, delivery) {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: MUTATION,
        variables: {
          cartId: `gid://shopify/Cart/${cart.token}`,
          buyerIdentity: { preferences: { delivery } },
        },
      }),
    });
    if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
    const { data, errors } = await res.json();
    const userErrors = data?.cartBuyerIdentityUpdate?.userErrors || [];
    if (errors?.length || userErrors.length) {
      throw new Error('cartBuyerIdentityUpdate failed: ' + JSON.stringify(errors || userErrors));
    }
    return data.cartBuyerIdentityUpdate.cart;
  }

  // The pickup generator only offers stores within range of user_latitude/longitude.
  // Use the store's own coords when they're missing or the chosen store is out of range.
  async function ensurePickupCoords(cart, store) {
    const storeLat = parseFloat(store?.store_latitude);
    const storeLon = parseFloat(store?.store_longitude);
    if (Number.isNaN(storeLat) || Number.isNaN(storeLon)) return;

    const userLat = parseFloat(cart.attributes?.user_latitude);
    const userLon = parseFloat(cart.attributes?.user_longitude);
    const hasCoords = !Number.isNaN(userLat) && !Number.isNaN(userLon);
    if (hasCoords && haversineMiles(userLat, userLon, storeLat, storeLon) <= MAX_PICKUP_MILES) return;

    const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { user_latitude: storeLat.toFixed(2), user_longitude: storeLon.toFixed(2) },
      }),
    });
    if (!res.ok) throw new Error('cart/update.js HTTP ' + res.status);
  }

  const PickupPreference = {
    // Shopify Location ID for a pickup_in_store_locations metaobject entry, or null.
    // The metaobject's store_id is the numeric Location ID.
    locationIdFor(store) {
      return normalizeLocationId(store?.store_id);
    },

    // Call before the add-to-cart popup opens: stops cart-attributes.liquid from
    // later overwriting the coords this module may write with the shopper's geoip.
    claimGeoAttributes() {
      storage.set(GEO_FLAG_KEY, 'true');
    },

    // After a successful BOPIS add-to-cart.
    async applyPickup(store) {
      try {
        const locationId = this.locationIdFor(store);
        if (!locationId) {
          console.warn('BOPIS: no store_id (Location ID) on store', store?.store_name);
          return;
        }
        const cart = await fetchCart();
        await ensurePickupCoords(cart, store);
        await updateDeliveryPreference(cart, { deliveryMethod: ['PICK_UP'], pickupHandle: [locationId] });
        storage.set(FLAG_KEY, locationId);
      } catch (err) {
        console.error('BOPIS: failed to set pickup preference', err);
      }
    },

    // Clears the preference once the cart has no BOPIS lines (or is empty).
    async reconcile() {
      if (!storage.get(FLAG_KEY)) return;
      try {
        const cart = await fetchCart();
        if (cart.items.some((item) => item.properties?._pickup_location)) return;
        await updateDeliveryPreference(cart, { deliveryMethod: [], pickupHandle: [] });
        storage.remove(FLAG_KEY);
      } catch (err) {
        console.error('BOPIS: failed to clear pickup preference', err);
      }
    },
  };

  window.PickupPreference = PickupPreference;
  window.addEventListener('cart:change', () => PickupPreference.reconcile());
  document.addEventListener('DOMContentLoaded', () => PickupPreference.reconcile());
})();
